import { stytchClient } from './stytch';
import { isOnPremMode, isStytchEnabled, oauth2LogoutRedirectUrl } from './runtimeConfig';
import { buildSafeSameOriginPathRedirectTarget, buildSafeSameOriginRedirectTarget } from './redirects';

const AUTH_LOG_PREFIX = '[auth]';
export const LOGIN_PROJECT_BOOTSTRAP_KEY = 'loginProjectBootstrappedForUser';
const DEFAULT_PROXY_LOGIN_PATH = '/oauth2/sign_in';
const DEFAULT_PROXY_LOGOUT_PATH = '/oauth2/sign_out';
const GRPC_PERMISSION_DENIED_CODE = 7;
const HTTP_UNAUTHORIZED_CODE = 401;
const HTTP_FORBIDDEN_CODE = 403;
export const AUTH_STATE_RESET_EVENT = 'hermetiq:auth-state-reset';

// Renewing an expired oauth2-proxy session must not loop: if a renewal was just
// attempted and the API still rejects us, fall back to a real sign-out.
const PROXY_RENEWAL_STORAGE_KEY = 'hermetiq:proxySessionRenewalAt';
const PROXY_RENEWAL_LOOP_WINDOW_MS = 60 * 1000;
const PROXY_RENEWAL_INFLIGHT_RESET_MS = 10 * 1000;

let logoutInFlight = null;
let proxyRenewalInFlight = false;
let _proxyIdToken = null;

export function setProxyIdToken(token) {
  _proxyIdToken = token;
}

function isBrowser() {
  return typeof window !== 'undefined';
}

function safeStorageRead(storage, key) {
  if (!isBrowser()) return null;
  try {
    return storage.getItem(key);
  } catch (error) {
    console.warn(`Failed to read storage key "${key}"`, error);
    return null;
  }
}

function safeStorageWrite(storage, key, value) {
  if (!isBrowser()) return;
  try {
    storage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to write storage key "${key}"`, error);
  }
}

function safeStorageRemove(storage, key) {
  if (!isBrowser()) return;
  try {
    storage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove storage key "${key}"`, error);
  }
}

function parseJwtPayload(token) {
  if (!token) return null;

  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const normalized = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('Failed to parse JWT payload', error);
    return null;
  }
}

function getCookieValue(name) {
  if (!isBrowser() || !document.cookie) return null;

  const cookieArray = document.cookie.split(';');
  for (const cookie of cookieArray) {
    const [cookieName, ...rest] = cookie.trim().split('=');
    if (cookieName === name) {
      return rest.join('=') || null;
    }
  }

  return null;
}

function getTokenFromStytchClient() {
  try {
    if (stytchClient?.session?.getTokens) {
      const tokens = stytchClient.session.getTokens();
      if (tokens?.session_jwt) {
        return {
          token: tokens.session_jwt,
          source: 'stytch.session.getTokens',
        };
      }
    }
  } catch (error) {
    console.warn('Failed reading token from stytch.session.getTokens()', error);
  }

  try {
    if (stytchClient?.session?.session_jwt) {
      return {
        token: stytchClient.session.session_jwt,
        source: 'stytch.session.session_jwt',
      };
    }
  } catch (error) {
    console.warn('Failed reading token from stytch.session.session_jwt', error);
  }

  return null;
}

function getSessionFromStytchClient() {
  try {
    if (stytchClient?.session?.getSync) {
      const session = stytchClient.session.getSync();
      if (session) {
        return {
          session,
          source: 'stytch.session.getSync',
        };
      }
    }
  } catch (error) {
    console.warn('Failed reading session from stytch.session.getSync()', error);
  }

  return null;
}

function getTokenFromLocalStorage() {
  const rawSession = safeStorageRead(window.localStorage, 'stytch_session');
  if (!rawSession) return null;

  try {
    const parsed = JSON.parse(rawSession);
    if (parsed?.session_jwt) {
      return {
        token: parsed.session_jwt,
        source: 'localStorage.stytch_session',
      };
    }
  } catch (error) {
    console.warn('Failed parsing localStorage stytch_session', error);
  }

  return null;
}

export function getSessionTokenDetails() {
  if (!isStytchEnabled) {
    const token = _proxyIdToken || null;
    const payload = parseJwtPayload(token);
    const expiresAt = payload?.exp ? new Date(payload.exp * 1000).toISOString() : null;
    const secondsUntilExpiry = payload?.exp ? Math.round((payload.exp * 1000 - Date.now()) / 1000) : null;

    return {
      token,
      source: 'oauth2-proxy',
      expiresAt,
      secondsUntilExpiry,
      sessionSource: 'oauth2-proxy',
      sessionExpiresAt: null,
      sessionSecondsUntilExpiry: null,
      sessionId: null,
      subject: payload?.sub || null,
    };
  }

  const sessionMatch = getSessionFromStytchClient();
  const session = sessionMatch?.session || null;
  const tokenMatch =
    getTokenFromStytchClient() ||
    (() => {
      const cookieToken = getCookieValue('stytch_session_jwt');
      return cookieToken ? { token: cookieToken, source: 'cookie.stytch_session_jwt' } : null;
    })() ||
    (isBrowser() ? getTokenFromLocalStorage() : null);

  const token = tokenMatch?.token || null;
  const payload = parseJwtPayload(token);
  const expiresAt = payload?.exp ? new Date(payload.exp * 1000).toISOString() : null;
  const secondsUntilExpiry = payload?.exp ? Math.round((payload.exp * 1000 - Date.now()) / 1000) : null;
  const sessionExpiresAt = session?.expires_at || null;
  const sessionSecondsUntilExpiry = sessionExpiresAt
    ? Math.round((new Date(sessionExpiresAt).getTime() - Date.now()) / 1000)
    : null;

  return {
    token,
    source: tokenMatch?.source || null,
    expiresAt,
    secondsUntilExpiry,
    sessionSource: sessionMatch?.source || null,
    sessionExpiresAt,
    sessionSecondsUntilExpiry,
    sessionId: session?.session_id || null,
    subject: payload?.sub || payload?.user_id || null,
  };
}

export function getSessionToken() {
  return getSessionTokenDetails().token;
}

export function withHermetiqJwt(headers = {}, token = getSessionToken()) {
  if (!token) {
    return { ...headers };
  }

  return {
    ...headers,
    'x-hermetiq-jwt': token,
  };
}

export function logAuthEvent(eventName, details = {}) {
  if (!isBrowser()) return;

  console.info(`${AUTH_LOG_PREFIX} ${eventName}`, {
    ...details,
    path: window.location.pathname,
    search: window.location.search,
  });
}

function getCurrentPathTarget() {
  return `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
}

function isPermissionDeniedError(err) {
  const code = Number(err?.code);
  const httpStatus = Number(err?.status ?? err?.statusCode ?? err?.response?.status);
  const statusCode = err?.statusCode?.toString?.().toUpperCase?.() || '';

  return (
    code === GRPC_PERMISSION_DENIED_CODE ||
    code === HTTP_FORBIDDEN_CODE ||
    httpStatus === HTTP_FORBIDDEN_CODE ||
    statusCode === 'PERMISSION_DENIED'
  );
}

export function isExpiredOauth2ProxyToken() {
  const tokenDetails = getSessionTokenDetails();
  if (!tokenDetails?.token || tokenDetails.source !== 'oauth2-proxy') {
    return false;
  }

  if (typeof tokenDetails.secondsUntilExpiry === 'number') {
    return tokenDetails.secondsUntilExpiry <= 0;
  }

  if (tokenDetails.expiresAt) {
    const expiresAtMs = new Date(tokenDetails.expiresAt).getTime();
    return Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now();
  }

  return false;
}

export function isUnauthenticatedError(err) {
  if (!err) return false;

  const message = err.message?.toString?.().toLowerCase?.() || '';
  const grpcMessage = err.metadata?.get?.('grpc-message')?.toString?.().toLowerCase?.() || '';
  const combined = `${message} ${grpcMessage}`;

  return err.code === 16 ||
    err.code === HTTP_UNAUTHORIZED_CODE ||
    (isPermissionDeniedError(err) && isExpiredOauth2ProxyToken()) ||
    combined.includes('invalid session jwt') ||
    combined.includes('invalid user jwt') ||
    combined.includes('no jwt found') ||
    combined.includes('session auth failed') ||
    combined.includes('idp introspection failed') ||
    combined.includes('unauthenticated');
}

function buildLoginUrl({ preserveCurrentPath = true } = {}) {
  if (!isBrowser()) return '/login';

  if (!isStytchEnabled) {
    // A relative path: oauth2-proxy only honours absolute rd targets whose host
    // is in its whitelist (the login origin), so an absolute same-host URL was
    // silently dropped and every renewal landed on /.
    const targetPath = preserveCurrentPath ? getCurrentPathTarget() : '/';
    const redirectTarget = buildSafeSameOriginPathRedirectTarget(targetPath, '/');
    return `${DEFAULT_PROXY_LOGIN_PATH}?rd=${encodeURIComponent(redirectTarget)}`;
  }

  const params = new URLSearchParams();
  const currentUrl = getCurrentPathTarget();

  if (preserveCurrentPath && window.location.pathname !== '/login') {
    params.set('redirect_url', currentUrl);
  }

  const queryString = params.toString();
  return queryString ? `/login?${queryString}` : '/login';
}

function buildOauth2LogoutRedirectTarget({ preserveCurrentPath = false } = {}) {
  const configuredRedirectUrl = String(oauth2LogoutRedirectUrl || '').trim();
  if (configuredRedirectUrl) {
    return new URL(configuredRedirectUrl, window.location.origin).toString();
  }

  const targetPath = preserveCurrentPath ? getCurrentPathTarget() : '/';
  return buildSafeSameOriginRedirectTarget(targetPath);
}

export function clearLoginBootstrapState() {
  if (!isBrowser()) return;
  safeStorageRemove(window.sessionStorage, LOGIN_PROJECT_BOOTSTRAP_KEY);
}

function notifyAuthStateReset(reason) {
  _proxyIdToken = null;
  if (!isBrowser()) return;

  try {
    window.dispatchEvent(new CustomEvent(AUTH_STATE_RESET_EVENT, { detail: { reason } }));
  } catch (error) {
    console.warn('Failed to notify auth state reset', error);
  }
}

export async function logout({
  reason = 'manual_logout',
  revokeSession = true,
  preserveCurrentPath = false,
} = {}) {
  if (logoutInFlight) {
    return logoutInFlight;
  }

  logoutInFlight = (async () => {
    logAuthEvent('logout:start', {
      reason,
      revokeSession,
      preserveCurrentPath,
      authMode: isOnPremMode ? 'on-prem' : (isStytchEnabled ? 'stytch' : 'oauth2-proxy'),
    });

    clearLoginBootstrapState();
    notifyAuthStateReset(reason);

    if (!isOnPremMode && isStytchEnabled && revokeSession && stytchClient?.session?.revoke) {
      try {
        await stytchClient.session.revoke();
        logAuthEvent('logout:revoked-session', { reason });
      } catch (error) {
        console.warn('Failed to revoke session during logout', error);
      }
    }

    if (isBrowser()) {
      const redirectUrl = isStytchEnabled && !isOnPremMode
        ? buildLoginUrl({ preserveCurrentPath })
        : `${DEFAULT_PROXY_LOGOUT_PATH}?rd=${encodeURIComponent(
            buildOauth2LogoutRedirectTarget({ preserveCurrentPath })
          )}`;
      logAuthEvent('logout:redirect', { reason, redirectUrl });
      window.location.assign(redirectUrl);
    }
  })().finally(() => {
    logoutInFlight = null;
  });

  return logoutInFlight;
}

// In cloud (oauth2-proxy BFF) mode the proxy session lives only as long as the
// access token (~1h). When it lapses the user still holds a valid Stytch SSO
// session, so re-run the proxy login (a redirect through the login origin that
// needs no interaction) instead of signing out: sign-out revokes the SSO session
// and would force an interactive login every time the token expires.
function canRenewProxySession() {
  if (!isBrowser() || isOnPremMode || isStytchEnabled) return false;
  const lastRenewalAt = Number(safeStorageRead(window.sessionStorage, PROXY_RENEWAL_STORAGE_KEY));
  return !(Number.isFinite(lastRenewalAt) && lastRenewalAt > 0
    && Date.now() - lastRenewalAt < PROXY_RENEWAL_LOOP_WINDOW_MS);
}

function renewProxySession(reason) {
  proxyRenewalInFlight = true;
  // A blocked navigation (e.g. an unsaved-changes prompt) must not wedge renewal.
  setTimeout(() => { proxyRenewalInFlight = false; }, PROXY_RENEWAL_INFLIGHT_RESET_MS);

  safeStorageWrite(window.sessionStorage, PROXY_RENEWAL_STORAGE_KEY, String(Date.now()));
  clearLoginBootstrapState();
  notifyAuthStateReset(reason);

  const redirectUrl = buildLoginUrl({ preserveCurrentPath: true });
  logAuthEvent('auth:renew-proxy-session', { reason, redirectUrl });
  window.location.assign(redirectUrl);
}

export async function handleAuthenticationFailure(reason, err, details = {}) {
  logAuthEvent('auth:failure', {
    reason,
    code: err?.code,
  });

  // Session replay records the console; a failed RPC's request body stays out.
  const { requestData: _omitted, ...loggable } = details;
  console.warn('Authentication failure', {
    reason,
    code: err?.code,
    message: err?.message,
    ...loggable,
  });

  // Check in-flight first: the first failure records the loop-guard timestamp, and
  // its siblings from the same lapse must not mistake that for a failed renewal.
  if (proxyRenewalInFlight) return;
  if (canRenewProxySession()) {
    renewProxySession(reason);
    return;
  }

  await logout({
    reason,
    revokeSession: true,
    preserveCurrentPath: true,
  });
}
